/**
 * ProposalReviewCard — one resolve proposal, shown as a before/after diff with
 * Approve / Reject / Revise.
 *
 * Distinct from ``ProposalCard`` (the chat-thread bubble, Approve/Reject only):
 * the review card leads with the DiffView and offers a third action — Revise —
 * that reveals an inline guidance box and streams a fresh rewrite. Pure
 * presentation: all state + apply logic lives in ``useProposalReviewFlow``.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import type { SectionProposal } from "../../api/client";
import { buildSnippet, parsePipeRow, type TableEditSnippet } from "../services/tableEdit";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import { DiffView } from "./DiffView";
import { TableDiffView } from "./TableDiffView";

/** Build the table-diff snippet for a TABLE proposal from its per-row edits.
 *  The header is the first parseable row of the whole-table ``original_text``
 *  (null when a marker sits in the header row itself, so it shows as data). */
function snippetForProposal(proposal: SectionProposal): TableEditSnippet | null {
  if (!proposal.is_table || !proposal.table_row_edits?.length) return null;
  let header: string[] | null = null;
  for (const line of (proposal.original_text || "").split("\n")) {
    const cells = parsePipeRow(line);
    if (cells) {
      header = cells;
      break;
    }
  }
  const includesHeaderRow = proposal.table_row_edits.some((e) => e.row_index === 0);
  const results = proposal.table_row_edits.map((e) => ({
    rowIndex: e.row_index,
    oldCells: parsePipeRow(e.original_row) ?? [],
    rawReplacement: e.proposed_row,
  }));
  return buildSnippet(results, includesHeaderRow ? null : header);
}

/**
 * True when the proposal would change nothing: the resolve pass couldn't find
 * what its flags ask for, and filed the span unchanged. Such a card has nothing
 * to approve — it is rendered as "couldn't resolve" with Reject / Revise only.
 * Exported for tests.
 */
export function isNoChangeProposal(proposal: SectionProposal): boolean {
  if (proposal.is_table) {
    const edits = proposal.table_row_edits ?? [];
    return edits.every((e) => e.original_row.trim() === e.proposed_row.trim());
  }
  return (proposal.proposed_text ?? "").trim() === (proposal.original_text ?? "").trim();
}

export interface ProposalReviewCardProps {
  proposal: SectionProposal;
  sectionNumber: string;
  sectionTitle: string;
  /** True while a decision (approve/reject) is being applied — disables actions. */
  busy?: boolean;
  /** True while a Revise re-run is streaming — keeps the card mounted, shows the
   *  live status line, and disables the actions. */
  revising?: boolean;
  /** Live status line while ``revising`` (from the rewrite's SSE events). */
  progressLabel?: string | null;
  onApprove: () => void;
  onReject: () => void;
  onRevise: (guidance: string) => void;
}

const useStyles = makeStyles({
  card: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    background: surfaceColors.paper,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    flexWrap: "wrap",
  },
  sectionNumber: {
    fontFamily: fonts.mono,
    fontSize: "11px",
    fontWeight: 700,
    color: inkColors.ink2,
    fontVariantNumeric: "tabular-nums",
  },
  sectionTitle: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
    flex: "1 1 auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  revisedChip: {
    fontFamily: fonts.mono,
    fontSize: "8.5px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    padding: "0 6px",
  },
  markers: {
    fontFamily: fonts.body,
    fontSize: "10px",
    color: inkColors.ink4,
  },
  // No-change proposal: the pass couldn't resolve its flags.
  unresolvedNote: {
    fontFamily: fonts.body,
    fontSize: "11px",
    lineHeight: 1.45,
    color: "#92400E",
    background: "#FFFBEB",
    border: "1px solid #FCD34D",
    borderRadius: "6px",
    padding: "6px 8px",
  },
  progress: {
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
    fontStyle: "italic",
  },
  reviseBox: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: "48px",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink,
    borderRadius: "4px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    padding: "6px 8px",
  },
  actions: {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  spacer: { flex: 1 },
  approveBtn: {
    border: "none",
    borderRadius: "4px",
    padding: "5px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: statusColors.cool,
    color: "#063D2B",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  secondaryBtn: {
    borderRadius: "4px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${inkColors.ink4}`,
    color: inkColors.ink2,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  rejectBtn: {
    borderRadius: "4px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
});

export const ProposalReviewCard: React.FC<ProposalReviewCardProps> = ({
  proposal,
  sectionNumber,
  sectionTitle,
  busy = false,
  revising = false,
  progressLabel = null,
  onApprove,
  onReject,
  onRevise,
}) => {
  const styles = useStyles();
  const [showRevise, setShowRevise] = useState(false);
  const [guidance, setGuidance] = useState("");
  const blocked = busy || revising;
  // Table proposals render a per-cell diff grid and drop Revise (which is
  // single-span only) — mirroring the chat table-edit card.
  const tableSnippet = snippetForProposal(proposal);
  const noChange = isNoChangeProposal(proposal);

  const submitRevise = () => {
    const g = guidance.trim();
    if (!g) return;
    onRevise(g);
    setGuidance("");
    setShowRevise(false);
  };

  return (
    <div className={styles.card} role="region" aria-label={`Proposed change in ${sectionNumber}`}>
      <div className={styles.header}>
        <span className={styles.sectionNumber}>{sectionNumber}</span>
        <span className={styles.sectionTitle}>{sectionTitle}</span>
        {proposal.revision_count > 0 ? (
          <span className={styles.revisedChip}>revised ×{proposal.revision_count}</span>
        ) : null}
      </div>
      {proposal.marker_texts.length > 0 ? (
        <div className={styles.markers}>
          {noChange ? "Couldn't resolve" : "Fills"}: {proposal.marker_texts.join(" · ")}
        </div>
      ) : null}
      {noChange ? (
        <div className={styles.unresolvedNote} role="note">
          Asthra couldn't find this information in your sources, so there's nothing to apply. Reject to keep the flag,
          or Revise with more guidance.
        </div>
      ) : null}

      {tableSnippet ? (
        <TableDiffView snippet={tableSnippet} />
      ) : (
        <DiffView before={proposal.original_text} after={proposal.proposed_text} />
      )}

      {revising && progressLabel ? <div className={styles.progress}>{progressLabel}</div> : null}

      {showRevise ? (
        <div className={styles.reviseBox}>
          <textarea
            className={styles.textarea}
            placeholder="Tell asthra how to rewrite this (it keeps the original and its current draft in context)…"
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            disabled={blocked}
            // biome-ignore lint/a11y/noAutofocus: focus the box the writer just opened
            autoFocus
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setShowRevise(false);
                setGuidance("");
              }}
              disabled={blocked}
            >
              Cancel
            </button>
            <button type="button" className={styles.approveBtn} onClick={submitRevise} disabled={blocked || !guidance.trim()}>
              Send
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.rejectBtn} onClick={onReject} disabled={blocked}>
            Reject
          </button>
          <span className={styles.spacer} />
          {tableSnippet ? null : (
            <button type="button" className={styles.secondaryBtn} onClick={() => setShowRevise(true)} disabled={blocked}>
              Revise
            </button>
          )}
          {noChange ? null : (
            <button type="button" className={styles.approveBtn} onClick={onApprove} disabled={blocked}>
              {busy ? "Applying…" : "Approve"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProposalReviewCard;
